import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { supabase } from '../utils/supabase';
import { useAuth } from '../lib/auth';
import { invoiceDocument, money, shortDate } from '../utils/invoices';
import { buildInvoicePdf } from '../utils/invoicePdf';
import InvoiceDocument from './invoices/InvoiceDocument';
import Loading from './Loading';

/** /i/:token — the client's view of an invoice (the "View invoice" link in the email). */
export default function PublicInvoice() {
  const { token } = useParams();
  const { session } = useAuth();
  const [state, setState] = useState({ loading: true, inv: null });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let active = true;
    supabase.rpc('public_invoice', { p_token: token }).then(({ data }) => {
      if (active) setState({ loading: false, inv: data || null });
    });
    return () => { active = false; };
  }, [token]);

  // Count the client's first view; signed-in visitors (you, checking the link) don't count
  useEffect(() => {
    if (session === null && state.inv) supabase.rpc('invoice_viewed', { p_token: token });
  }, [session, state.inv, token]);

  useEffect(() => {
    if (state.inv) document.title = `Invoice ${state.inv.number} · ${state.inv.seller?.name || ''}`;
  }, [state.inv]);

  if (state.loading) return <Loading />;
  const inv = state.inv;
  if (!inv) {
    return (
      <div className="min-h-screen bg-surface flex items-center justify-center px-4">
        <div className="bg-white rounded-2xl border border-line p-8 max-w-md text-center">
          <h1 className="text-xl font-bold">Invoice not found</h1>
          <p className="text-ink-muted mt-2">This link is not valid. Please contact the sender for a new link.</p>
        </div>
      </div>
    );
  }

  const doc = invoiceDocument({ inv: { ...inv, seller_snapshot: inv.seller, client_snapshot: inv.client }, lines: inv.lines });
  const balance = inv.total_cents - inv.paid_cents;
  const download = async () => {
    setBusy(true);
    try {
      const bytes = await buildInvoicePdf(doc);
      const url = URL.createObjectURL(new Blob([bytes], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `Invoice-${inv.number}.pdf`;
      a.click();
      setTimeout(() => URL.revokeObjectURL(url), 2000);
    } finally {
      setBusy(false);
    }
  };

  const banner = inv.status === 'void'
    ? { tone: 'bg-surface border-line text-ink-muted', text: 'This invoice has been voided. Nothing is due.' }
    : inv.status === 'paid'
      ? { tone: 'bg-blue-soft border-blue/30 text-ink', text: 'Paid in full. Thank you!' }
      : inv.overdue
        ? { tone: 'bg-coral-soft border-coral/40 text-ink', text: `${money(balance)} was due on ${shortDate(inv.due_date)}.` }
        : { tone: 'bg-blue-soft border-blue/30 text-ink', text: `${money(balance)} due by ${shortDate(inv.due_date)}.` };

  return (
    <div className="min-h-screen bg-surface px-4 py-8 sm:py-12">
      <div className="max-w-[860px] mx-auto space-y-4">
        <div className="flex flex-wrap items-center gap-3">
          <div className={`flex-1 min-w-[240px] border rounded-xl px-4 py-3 text-sm font-medium ${banner.tone}`}>{banner.text}</div>
          <button type="button" onClick={download} disabled={busy}
            className="h-11 px-4 rounded-xl bg-blue text-white font-semibold hover:bg-blue-dark disabled:opacity-50">
            {busy ? 'Preparing…' : 'Download PDF'}
          </button>
          <button type="button" onClick={() => window.print()}
            className="h-11 px-4 rounded-xl border border-line-strong bg-white text-ink font-semibold hover:bg-white/60 print:hidden">Print</button>
        </div>
        <InvoiceDocument doc={doc} />
        <p className="text-center text-xs text-ink-muted">Questions about this invoice? Reply to the email it came with{inv.seller?.email ? ` or write to ${inv.seller.email}` : ''}.</p>
      </div>
    </div>
  );
}
